import { Box, Flex, Input, InputGroup, InputLeftElement, Spinner } from "@chakra-ui/react";
import { Icons, Typography } from "@reconvert/react-ui-component";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useProductSelector, { getPickedProductIds } from "../../../../hooks/useProductSelector";
import SearchProductModal from "./AddConditionView/SearchProductModal";
import { ConditionFact, PageType } from "@reconvert/reconvert-utils";
import { APP_PLATFORM } from "../../../../config";
import { Platform } from "../../../../models";
import {
  compressImage,
  compressImageForWix,
  IFramePostMessageEvent,
  IFramePostMessageType,
  ShopifyResourcePickerType,
  sendPostMessage,
} from "../../../../utils/helpers";
import FieldError from "../FieldError";
import { selectAppState } from "../../../../app/slice/appSlice";
import { useAppSelector } from "../../../../app/hooks";

interface ProductSelectorProps {
  value: {
    productIds: string[];
    variantIds: string[];
    wholeProductIds?: string[];
    productCollectionIds?: { id: string; name: string }[];
  };
  setValue: (val: {
    productIds: string[];
    variantIds: string[];
    wholeProductIds?: string[];
    productCollectionIds?: { id: string; name: string }[];
  }) => void;
  conditionFact?: string;
  fieldError?: string;
}

const parseGid = (gid: string) => (typeof gid === "string" ? gid.split("/").pop() : "");

const GID = {
  product: (id: string) => `gid://shopify/Product/${id}`,
  collection: (id: string) => `gid://shopify/Collection/${id}`,
  variant: (id: string) => `gid://shopify/ProductVariant/${id}`,
};

const ProductSelector = ({ value, setValue, conditionFact, fieldError }: ProductSelectorProps) => {
  const { t } = useTranslation();
  const { isAppBridgePresent, activePage } = useAppSelector(selectAppState);
  const fact = conditionFact || ConditionFact.SPECIFIC_PRODUCT;
  // Variant-level targeting is only supported on post purchase for now. Every other page
  // targets whole products, so their pickers keep variant selection off.
  const isPostPurchasePage =
    activePage === PageType.POST_PURCHASE_PAGE1 || activePage === PageType.POST_PURCHASE_PAGE2;

  const [displayedProducts, setDisplayedProducts] = useState(4);
  const [isSearchProductModalShow, setIsSearchProductModalShow] = useState<boolean>(false);
  const resourcePickerRequestIdRef = useRef<string | null>(null);
  const resourcePickerTypeRef = useRef<ShopifyResourcePickerType | null>(null);

  const useShopifyResourcePicker = useMemo(
    () =>
      isAppBridgePresent &&
      (fact === ConditionFact.SPECIFIC_PRODUCT || fact === ConditionFact.PRODUCT_COLLECTION),
    [isAppBridgePresent, fact]
  );

  const resourcePickerType = useMemo((): ShopifyResourcePickerType => {
    if (fact === ConditionFact.PRODUCT_COLLECTION) return "collection";

    if (fact === ConditionFact.SPECIFIC_PRODUCT) return "product";

    return "product";
  }, [fact]);

  const {
    selectedProductIdObj,
    isProductsLoading,
    selectedProducts,
    selectedProductCollections,
    updateSelectedProductIdObj,
    handleRemoveProduct,
  } = useProductSelector({
    value,
    setValue,
    conditionFact: fact,
  });

  const resourcePickerSelectionIds = useMemo(() => {
    if (resourcePickerType === "collection") {
      const collections = selectedProductCollections ?? value?.productCollectionIds ?? [];

      return collections.map((c) => ({ id: GID.collection(c.id) }));
    }

    if (resourcePickerType === "product") {
      return getPickedProductIds(selectedProductIdObj ?? {}).map((productId) => {
        // Pages that open the picker with variants hidden must preselect products only. Their
        // rows still carry every variant, because loading a product fills in its variants.
        if (!isPostPurchasePage) {
          return { id: GID.product(productId) };
        }

        const variants = (selectedProductIdObj?.[productId] ?? []).map((vid) => ({ id: GID.variant(vid) }));

        // A whole-product selection preselects the product itself, with no variants listed.
        return variants.length > 0
          ? { id: GID.product(productId), variants }
          : { id: GID.product(productId) };
      });
    }

    return [];
  }, [
    resourcePickerType,
    isPostPurchasePage,
    selectedProductIdObj,
    selectedProductCollections,
    value?.productCollectionIds,
  ]);

  useEffect(() => {
    if (!useShopifyResourcePicker) return;

    const handleResourcePickerResponse = (event: MessageEvent) => {
      const { event: msgEvent, requestId, selection, type: responseType } = event.data || {};

      if (msgEvent !== IFramePostMessageEvent.SHOPIFY_RESOURCE_PICKER_RESPONSE) return;

      if (requestId !== resourcePickerRequestIdRef.current) return;

      const pickerType = responseType ?? resourcePickerTypeRef.current;

      resourcePickerRequestIdRef.current = null;
      resourcePickerTypeRef.current = null;

      if (!Array.isArray(selection)) return;

      if (pickerType === "collection") {
        const collectionData = selection
          .map((item: { id?: string; title?: string }) => ({
            id: parseGid(item?.id ?? ""),
            name: (item as { title?: string }).title ?? "",
          }))
          .filter((c): c is { id: string; name: string } => Boolean(c.id));

        const collectionObj = collectionData.reduce(
          (acc: Record<string, string[]>, item) => {
            acc[item.id] = [item.name];

            return acc;
          },
          {}
        );

        updateSelectedProductIdObj(collectionObj);

        return;
      }

      if (pickerType === "variant") {
        const productIdObj: Record<string, string[]> = {};

        selection.forEach((item: { id?: string; productId?: string } & { variants?: { id?: string }[] }) => {
          const variantId = parseGid(item?.id ?? "");
          const productId = parseGid((item as { productId?: string }).productId ?? "");

          if (!variantId) return;

          const pid = productId || variantId;

          if (!productIdObj[pid]) productIdObj[pid] = [];

          productIdObj[pid].push(variantId);
        });

        updateSelectedProductIdObj(productIdObj);

        return;
      }

      const productIdObj: Record<string, string[]> = {};
      const wholeProductHints: Record<string, boolean> = {};

      selection.forEach((item: { id?: string; totalVariants?: number; variants?: { id?: string }[] }) => {
        const productId = parseGid(item?.id ?? "");

        if (!productId) return;

        // Pickers opened without variant selection return no variants. Leaving the list empty
        // marks a whole-product selection, whose variants are resolved when the rule is evaluated.
        const variants = item?.variants ?? [];
        const variantIds = variants.map((v) => parseGid(v?.id ?? "")).filter(Boolean) as string[];

        productIdObj[productId] = variantIds;

        // The picker reports how many variants the product has, so a selection covering all of
        // them is recognised right away instead of waiting for the product's variants to load.
        if (typeof item?.totalVariants === "number") {
          wholeProductHints[productId] = variantIds.length >= item.totalVariants;
        }
      });

      updateSelectedProductIdObj(productIdObj, wholeProductHints);
    };

    window.addEventListener("message", handleResourcePickerResponse);

    return () => window.removeEventListener("message", handleResourcePickerResponse);
  }, [useShopifyResourcePicker, updateSelectedProductIdObj]);

  const handleLoadMore = useCallback(() => {
    setDisplayedProducts(displayedProducts + 4);
  }, [displayedProducts]);

  const moreProductsText = useMemo(() => {
    const text = conditionFact === ConditionFact.SPECIFIC_PRODUCT ? `product` : `collection`;
    const totalSelectedProducts =
      conditionFact === ConditionFact.SPECIFIC_PRODUCT
        ? [...selectedProducts]?.length
        : [...(selectedProductCollections || [])]?.length;

    if (totalSelectedProducts < 4) {
      return "";
    }

    const remainingProducts = Math.max(0, totalSelectedProducts - displayedProducts);

    if (remainingProducts === 0) {
      return "";
    }

    if (remainingProducts === 1) {
      return t(`+ 1 more ${text}`);
    }

    return `+ ${remainingProducts} ${t(`more ${text}s`)}`;
  }, [conditionFact, displayedProducts, selectedProductCollections, selectedProducts, t]);

  const productList = useMemo(() => {
    return conditionFact === ConditionFact.SPECIFIC_PRODUCT
      ? selectedProducts?.slice(0, displayedProducts)
      : selectedProductCollections?.slice(0, displayedProducts);
  }, [conditionFact, displayedProducts, selectedProductCollections, selectedProducts]);

  const getVariantCountText = useCallback(
    (product: { id?: string; variants?: { id: string; title?: string }[] }) => {
      if (fact !== ConditionFact.SPECIFIC_PRODUCT || !isPostPurchasePage) {
        return "";
      }

      const totalVariants = product?.variants?.length ?? 0;
      const selectedVariantIds = selectedProductIdObj?.[product?.id ?? ""] ?? [];
      const selectedVariantCount = selectedVariantIds.length;

      // A product with a single (default) variant has nothing to disambiguate.
      if (totalVariants <= 1 || !selectedVariantCount) {
        return "";
      }

      if (selectedVariantCount >= totalVariants) {
        return t("All variants");
      }

      if (selectedVariantCount === 1) {
        const selectedVariantTitle = product?.variants?.find(v => v.id === selectedVariantIds[0])?.title;

        return selectedVariantTitle || t("{{variantCount}} variant", { variantCount: 1 });
      }

      return t("{{variantCount}} variants", { variantCount: selectedVariantCount });
    },
    [fact, isPostPurchasePage, selectedProductIdObj, t]
  );

  // The search modal has no variant granularity: ticking a product selects every variant it has.
  // Products it adds therefore target the whole product, while products it leaves in place keep
  // whatever selection they already had.
  const getSearchModalWholeProductHints = useCallback(
    (productIdObj: Record<string, string[]>) => {
      if (fact !== ConditionFact.SPECIFIC_PRODUCT) {
        return undefined;
      }

      const hints: Record<string, boolean> = {};

      Object.keys(productIdObj).forEach((productId) => {
        if (!selectedProductIdObj?.[productId]) hints[productId] = true;
      });

      return hints;
    },
    [fact, selectedProductIdObj]
  );

  const imageSrc = useCallback((imageUrl: string) => {
    if (imageUrl) {
      return APP_PLATFORM === Platform.Shopify ? compressImage(imageUrl, 36) : compressImageForWix(imageUrl, 36);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compressImage, compressImageForWix]);

  const hasError = useMemo(
    () => typeof fieldError === "string" && fieldError.trim() !== "" && fieldError !== undefined,
    [fieldError]
  );
  const displayError = hasError ? fieldError : undefined;
  

  return (
    <>
      <Flex mt="8px" gap={1} flexDirection={"column"}>
        <InputGroup
          onClick={() => {
            if (useShopifyResourcePicker) {
              const requestId = `resource-picker-${Date.now()}-${Math.random().toString(36).slice(2)}`;
              const hasSelection = resourcePickerSelectionIds.length > 0;

              resourcePickerRequestIdRef.current = requestId;
              resourcePickerTypeRef.current = resourcePickerType;
              sendPostMessage(IFramePostMessageType.OPEN_RESOURCE_PICKER, {
                requestId,
                type: resourcePickerType,
                multiple: true,
                action: hasSelection ? "select" : "add",
                selectionIds: resourcePickerSelectionIds,
                filter:
                  resourcePickerType === "product"
                    ? { variants: isPostPurchasePage }
                    : undefined,
              });
            } else {
              setIsSearchProductModalShow(true);
            }
          }}
        >
          <InputLeftElement>
            <Icons.Search color="neutrals.600" />
          </InputLeftElement>
          <Input
            cursor={"pointer"}
            placeholder={
              conditionFact === ConditionFact.SPECIFIC_PRODUCT
                ? t("Search for products")
                : APP_PLATFORM === Platform.Shopify
                  ? t("Search for product collections")
                  : t("Search for product categories")
            }
            _hover={{
              border: "2px solid neutrals.600",
            }}
            _focus={{
              borderColor: "neutrals.600",
            }}
            isInvalid={hasError}
          />
        </InputGroup>
        {displayError && <FieldError error={displayError} />}
      </Flex>

      {isProductsLoading ? (
        <Flex justifyContent={"center"} mt="12px">
          <Spinner />
        </Flex>
      ) : (
        productList &&
        productList?.length > 0 && (
          <Flex
            width={"100%"}
            borderRadius={"4px"}
            borderLeft={"1px solid"}
            borderRight={"1px solid"}
            borderBottom={"1px solid"}
            borderColor={"neutrals.600"}
            flexDirection={"column"}
          >
            <Box maxHeight={"300px"} overflowY={"auto"} mr={"4px"}>
              {productList?.map((item: any, key: any) => {
                const variantCountText = getVariantCountText(item);

                return (
                  <Flex px="24px" py="14px" key={"item-" + key} justifyContent={"space-between"} alignItems={"center"}>
                    <Flex gap="12px">
                      {conditionFact === ConditionFact.SPECIFIC_PRODUCT && (
                        <>
                          {item?.images?.length > 0 ? (
                            <img src={imageSrc(item?.images[0])} alt="" height={"36px"} width={"36px"} />
                          ) : (
                            <Icons.ImagePreview w="36px" h="36px" />
                          )}
                        </>
                      )}

                      <Box>
                        <Typography lineHeight={"20px"} variant="subheading2" overflowWrap={"anywhere"}>
                          {item?.title ?? item?.name}
                        </Typography>
                        {variantCountText && (
                          <Typography mt={0} variant="xsmall" color={"neutrals.700"} overflowWrap={"anywhere"}>
                            {variantCountText}
                          </Typography>
                        )}
                      </Box>
                    </Flex>
                    <Icons.CloseIcon
                      height={"16px"}
                      width={"16px"}
                      cursor={"pointer"}
                      onClick={() => {
                        handleRemoveProduct(item?.id);
                      }}
                    />
                  </Flex>
                );
              })}
            </Box>
          </Flex>
        )
      )}
      {moreProductsText && (
        <Flex mt={"8px"} justifyContent={"end"}>
          <Typography
            onClick={handleLoadMore}
            cursor={"pointer"}
            variant="body"
            color={"link.color"}
            lineHeight={"16px"}
            fontWeight={500}
          >
            {moreProductsText}
          </Typography>
        </Flex>
      )}
      {isSearchProductModalShow && (
        <SearchProductModal
          value={
            conditionFact === ConditionFact.SPECIFIC_PRODUCT
              ? selectedProductIdObj
              : selectedProductCollections?.reduce((acc, item) => {
                acc[item.id] = acc[item.id] || [];
                acc[item.id].push(item.name);
                return acc;
              }, {} as any)
          }
          setValue={(data, _options) => {
            updateSelectedProductIdObj(data, getSearchModalWholeProductHints(data));
            setIsSearchProductModalShow(false);
          }}
          handleOnClose={() => setIsSearchProductModalShow(false)}
          conditionFact={conditionFact}
        />
      )}
    </>
  );
};

export default ProductSelector;
